import React, { useMemo, useEffect, useState } from "react";
import "./styles.css";
import { faArrowLeft, faFileExport } from "@fortawesome/free-solid-svg-icons";
import FloatingActions, { FloatingAction } from "../FloatingActions";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, IDatasource, IGetRowsParams } from "ag-grid-community";
import { themeQuartz } from "ag-grid-community";
import { RouterStoreItemKey } from "@/store/useRouter/types";
import useRouter from "@/store/useRouter";
import { useShallow } from "zustand/shallow";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import useAppData from "@/store/useAppData";
import { AG_GRID_LOCALE_CZ } from '@ag-grid-community/locale';
import { FetchItemsFn, FilterKind, Item, UiColumn } from "../ItemList/indexOld";
import Card from "../Card";
import DeicingLogFilterDialogWindow, { DeicingLogFilterData } from "../DeicingEcoFilterDialogWindow";
import { ExcelColumn, exportItemsToExcel } from "@/Utils/exportInventoryResultToExcel";
import StockMoveExcelTechDeiceFilterDialogWindow from "../StockMoveExcelTechDeiceFilterWindow";

const myTheme = themeQuartz.withParams({
    fontSize: 17,
    headerFontSize: 17,
    rowHeight: 55,
    headerHeight: 48,
});

function mapFilter(filter?: FilterKind): ColDef["filter"] {
    if (filter === "text") return "agTextColumnFilter";
    if (filter === "number") return "agNumberColumnFilter";
    if (filter === "set") return "agSetColumnFilter";
    if (filter === "date") return "agDateColumnFilter";
    return false;
}

const HistoryItemsOdmraz: React.FC = () => {

    const [agParamsState, setAgParamsState] = useState<any | null>(null);
    const [openExcelTechDeiceWindow, setOpenExcelTechDeiceWindow] = useState<boolean>(false);

    // const dateFormat = (date: string, dateOnly?: boolean) => {
    //     const d = new Date(date);

    //     const pad = (n: number) => String(n).padStart(2, "0");

    //     if (dateOnly) {
    //         return (
    //             pad(d.getDate()) + "." +
    //             pad(d.getMonth() + 1) + "." +
    //             d.getFullYear()
    //         );
    //     } else {
    //         return (
    //             pad(d.getDate()) + "." +
    //             pad(d.getMonth() + 1) + "." +
    //             d.getFullYear() + " " +
    //             pad(d.getHours()) + ":" +
    //             pad(d.getMinutes()) + ":" +
    //             pad(d.getSeconds())
    //         );
    //     }
    // };

    const dateFormat = (date: string, dateOnly?: boolean) => {
        const d = new Date(date);

        const pad = (n: number) => String(n).padStart(2, "0");

        const formattedDate =
            pad(d.getUTCDate()) + "." +
            pad(d.getUTCMonth() + 1) + "." +
            d.getUTCFullYear();

        if (dateOnly) {
            return formattedDate;
        }

        return (
            formattedDate + " " +
            pad(d.getUTCHours()) + ":" +
            pad(d.getUTCMinutes()) + ":" +
            pad(d.getUTCSeconds())
        );
    };

    const actionFormat = (value: string) => {
        if (value === "inventory") {
            return "inventura";
        }

        if (value === "techDeicing") {
            return "technologický odmraz";
        }

        if (value === "odmraz") {
            return "odmraz";
        }
    };

    const columns: UiColumn<Item>[] = [
        { field: "id", headerName: "Id", flex: 1, filter: "number", floatingFilter: true },
        { field: "name", headerName: "Název", flex: 2, filter: "text", floatingFilter: true },
        { field: "stockName", headerName: "Sklad", flex: 1, filter: "text", floatingFilter: true },
        { field: "movement", headerName: "Pohyb", flex: 1, filter: "text", floatingFilter: true },
        {
            field: "log_action",
            headerName: "Akce",
            flex: 1,
            filter: "text",
            floatingFilter: true,
            valueFormatter: params => {
                if (!params.value) return "";

                return actionFormat(params.value);
            }
        },
        { field: "quantity", headerName: "Počet", flex: 1, filter: "number", floatingFilter: true },
        {
            field: "date",
            headerName: "Datum",
            flex: 1,
            filter: 'date',
            floatingFilter: true,
            valueFormatter: params => {
                if (!params.value) return "";

                return dateFormat(params.value);
            }
        },
        { field: "appuser", headerName: "Provedl", flex: 1, filter: "text", floatingFilter: true },
        { field: "note", headerName: "Poznámka", flex: 1, filter: "text", floatingFilter: true },
    ];

    const selectedStock = useAppData(state => state.selectedStock);

    const router = useRouter(
        useShallow(state => ({
            go: state.go,
            history: state.history
        }))
    );

    const actions: FloatingAction[] = [
        {
            key: "2",
            label: "Excel (Vše)",
            icon: <FontAwesomeIcon icon={faFileExport} />,
            onClick: () => onPrintLogExcel(agParamsState)
        },
        {
            key: "3",
            label: "Excel (Tech. odmraz)",
            icon: <FontAwesomeIcon icon={faFileExport} />,
            onClick: () => setOpenExcelTechDeiceWindow(true)
        },
        {
            key: "1",
            label: "Zpět",
            icon: <FontAwesomeIcon icon={faArrowLeft} />,
            onClick: () => router.go({ key: RouterStoreItemKey.LISTITEMS })
        }
    ]

    const columnDefs = useMemo<ColDef<Item>[]>(() => {
        return columns.map((c) => ({
            field: c.field as any,
            headerName: c.headerName,
            flex: c.flex,
            filter: mapFilter(c.filter),
            floatingFilter: c.floatingFilter,
            valueFormatter: c.valueFormatter
        }));
    }, [columns]);

    const datasource = useMemo<IDatasource>(() => ({
        getRows: async (params: IGetRowsParams) => {
            try {
                setAgParamsState(params);

                const result = await fetchItems({
                    startRow: params.startRow,
                    endRow: params.endRow,
                    agParams: { stockId: selectedStock?.id, ...params }
                });

                params.successCallback(result.rows, result.totalCount);
            } catch {
                params.failCallback();
            }
        },
    }), []);

    const onPrintLogExcel = async (agParams?: any) => {
        const deicingExcelColumns: ExcelColumn<any>[] = [
            {
                header: "Název položky",
                field: "name",
                width: 18,
                // align: "center",
            },
            {
                header: "Sklad",
                field: "stockName",
                width: 22,
                // align: "center",
            },
            {
                header: "Pohyb",
                field: "movement",
                width: 15,
                // align: "center",
            },
            {
                header: "Akce",
                field: "log_action",
                width: 18,
                getValue: row => {
                    if (!row.log_action) return "";

                    return actionFormat(row.log_action);
                }
                // align: "center",
            },
            {
                header: "Počet",
                field: "quantity",
                type: "number",
                width: 12,
                // align: "center",
            },
            {
                header: "Datum",
                field: "date",
                type: "date",
                width: 18,
                // align: "center",
                getValue: row => {
                    if (row.date == null) return null;

                    return dateFormat(row.date);
                }
            },
            {
                header: "Provedl",
                field: "appuser",
                width: 15,
                // align: "center",
            },
            {
                header: "Poznámka",
                field: "note",
                width: 15,
                // align: "center",
            }
        ];

        const filteredData = await getStockItems(0, 1000000, agParams ?? {});

        exportItemsToExcel(
            filteredData?.rows ?? [],
            deicingExcelColumns,
            {
                sheetName: "Skladové pohyby (Odmrazování)",
                fileName: "Skladové pohyby (Odmrazování).xlsx",
                title: "Skladové pohyby (Odmrazování)",
            }
        );
    }

    const fetchItems: FetchItemsFn<Item> = async ({ startRow, endRow, agParams }) => {
        const { rows, totalCount } = await getStockItems(startRow, endRow, { ...agParams });
        return { rows, totalCount };
    };

    const getStockItems = async (startRow, endRow, agParams) => {
        try {
            console.log(agParams);
            const res = await (
                axios({
                    method: "post",
                    url: "api/log/getAll",
                    data: {
                        //filter: fm,
                        //sort: params.sortModel,
                        start: startRow,
                        end: endRow,
                        moduleCode: "ODMRAZ",
                        filterModel: agParams.filterModel,
                        sortModel: agParams.sortModel,
                        withoutQuantity: agParams?.withoutQuantity,
                        // from: agParams?.from,
                        // to: agParams?.to
                    }
                })
            );

            return {
                rows: res.data.stocksItems,
                totalCount: res.data.totalCount
            };
        } catch (err) {
            console.log(err);
            throw err;
        }
    }

    return <>
        {openExcelTechDeiceWindow &&
            <StockMoveExcelTechDeiceFilterDialogWindow
                onCloseDialog={() => setOpenExcelTechDeiceWindow(false)}
                onConfirmDialog={(data) => {

                    const agParamsData = {
                        filterModel: {
                            stockMain: {
                                filter: true,
                                filterType: "boolean",
                                type: "notEqual"
                            },
                            movement: {
                                filter: "vydání",
                                filterType: "text",
                                type: "equals"
                            },
                            log_action: {
                                filter: "technologický",
                                filterType: "text",
                                type: "contains"
                            },
                            ...(!data?.name ? {} : {
                                stockName: {
                                    filter: data?.name,
                                    filterType: "text",
                                    type: "contains"
                                }
                            })
                        }
                    }

                    onPrintLogExcel(agParamsData);

                    setOpenExcelTechDeiceWindow(false);
                }}
            >
            </StockMoveExcelTechDeiceFilterDialogWindow>}
        <div className={"inventory"}><Card
            title="Skladové pohyby"
        >
            <div className="item-list-card">
                <div className="item-list-grid-wrapper">
                    <AgGridReact
                        theme={myTheme}
                        columnDefs={columnDefs}
                        rowModelType="infinite"
                        cacheBlockSize={100}
                        datasource={datasource}
                        localeText={AG_GRID_LOCALE_CZ}
                        enableCellTextSelection={true}
                        ensureDomOrder={true}
                    />
                </div>

                {
                    actions?.length ? (
                        <div className="item-list-actions">
                            <FloatingActions actions={actions} />
                        </div>
                    ) : null
                }
            </div></Card ></div>
    </>;
};

export default HistoryItemsOdmraz;
